import { defineComponent as _defineComponent } from 'vue'
import EditIcon from '@/components/molecules/EditIcon.vue';
import useI18n from '@/mixins/useI18n';
import useListTools from '@/mixins/useListTools';
import { CertificateListItem, EducationListItem, ProfileMaxChars } from '@/models/Profile';
import { updateCertificates, updateEducations } from '@/services/Profile/Api';
import useProfileState from '@/state/profile/profileState';
import AddIcon from '@/views/profile/components/AddIcon.vue';
import ProfileEditActionButtons from '@/views/profile/components/ProfileEditActionButtons.vue';
import {
  computed, onMounted, PropType, ref, toRefs
} from 'vue';
import { cloneDeep } from 'lodash';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileCertificatesEducationsEditor',
  props: {
  type: {
    type: String as PropType<'certificates' | 'educations'>,
    default: 'certificates'
  }
},
  setup(__props) {

const props = __props;



const fullYear = new Date().getFullYear();
const MIN_YEAR = 1950;
const MAX_YEAR = 2040;
const { type } = toRefs(props);
const { t } = useI18n();
const { profile } = useProfileState();
const labelToAdd = ref('');
const yearToAdd = ref(`${fullYear}`);
const originToAdd = ref('');
const linkToAdd = ref('');

const certificatesEducations = ref<CertificateListItem[] | EducationListItem[]>([]);
const {
  addItem: addToList,
  deleteItem,
  moveUp
} = useListTools<CertificateListItem | EducationListItem>(certificatesEducations);

const normalizeYear = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;

  const numericYear = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(numericYear)) return undefined;
  if (numericYear < MIN_YEAR || numericYear > MAX_YEAR) return undefined;

  return numericYear;
};

const toInputYear = (value?: number | null) => (value && value > 0 ? value : '');

const updateItemYear = (item: CertificateListItem | EducationListItem, event: Event) => {
  (item as CertificateListItem & EducationListItem & { year?: number | string | null }).year = (event.target as HTMLInputElement).value;
};

const normalizeItemYear = (item: CertificateListItem | EducationListItem) => {
  item.year = normalizeYear(item.year);
};

const normalizeYearToAdd = () => {
  const normalizedYear = normalizeYear(yearToAdd.value);
  yearToAdd.value = normalizedYear ? `${normalizedYear}` : '';
};

const sanitizeCertificatesEducations = (list: (CertificateListItem[] | EducationListItem[])) => list.map((item) => {
  const normalizedYear = normalizeYear(item.year);
  return {
    ...item,
    year: normalizedYear,
  };
});

const getOriginalList = () => (type.value === 'certificates'
  ? sanitizeCertificatesEducations(cloneDeep(profile.value?.certificates?.certificatesList ?? [])) as CertificateListItem[]
  : sanitizeCertificatesEducations(cloneDeep(profile.value?.educations?.educationsList ?? [])) as EducationListItem[]);

onMounted(() => {
  certificatesEducations.value = getOriginalList();
});

const itemLabel = computed(() => t(`profile.certificatesEducations.itemLabel.${type.value}`));
const originLabel = computed(() => t(`profile.certificatesEducations.originLabel.${type.value}`));

const canAdd = computed(() => !!labelToAdd.value && !!originToAdd.value && (yearToAdd.value === '' || normalizeYear(yearToAdd.value) !== undefined));

const addItem = () => {
  if (!canAdd.value) return;

  const normalizedYear = normalizeYear(yearToAdd.value);
  addToList({
    issuer: originToAdd.value,
    institute: originToAdd.value,
    label: labelToAdd.value,
    link: linkToAdd.value,
    year: normalizedYear,
  });
  labelToAdd.value = '';
  yearToAdd.value = `${fullYear}`;
  originToAdd.value = '';
  linkToAdd.value = '';
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const stripId = (list: (CertificateListItem[] | EducationListItem[])) => list.map(({ externalId, year, ...rest }) => {
  const normalizedYear = normalizeYear(year);
  return normalizedYear === undefined
    ? { ...rest }
    : { ...rest, year: normalizedYear };
});

const save = async () => {
  if (canAdd.value) {
    addItem();
  }
  certificatesEducations.value = sanitizeCertificatesEducations(certificatesEducations.value) as CertificateListItem[] | EducationListItem[];
  const strippedAndSorted = stripId(certificatesEducations.value);
  if (type.value === 'certificates') {
    await updateCertificates({
      certificates: {
        certificatesList: strippedAndSorted as CertificateListItem[]
      }
    });
  } else {
    await updateEducations({
      educations: {
        educationsList: strippedAndSorted as EducationListItem[]
      }
    });
  }
};

const maxLengthItem = computed(() => (type.value === 'certificates' ? ProfileMaxChars.CERTIFICATE : ProfileMaxChars.DIPLOMA));

return { __sfc: true,props, fullYear, MIN_YEAR, MAX_YEAR, type, t, profile, labelToAdd, yearToAdd, originToAdd, linkToAdd, certificatesEducations, addToList, deleteItem, moveUp, normalizeYear, toInputYear, updateItemYear, normalizeItemYear, normalizeYearToAdd, sanitizeCertificatesEducations, getOriginalList, itemLabel, originLabel, canAdd, addItem, stripId, save, maxLengthItem, EditIcon, ProfileMaxChars, AddIcon, ProfileEditActionButtons }
}

})